import type { Translations } from "./en";

/**
 * Marathi dictionary — formal/administrative (municipal-office) register.
 *
 * TODO(mr-review): a handful of terms are flagged below rather than silently
 * guessed, because a municipal/revenue-department document may already use an
 * established term that differs from a direct translation:
 *   - "Zone" / "Ward" / "Survey Block" → translated here as झोन / प्रभाग / ब्लॉक.
 *     "प्रभाग" is the common Marathi word for a municipal ward; "झोन" and
 *     "ब्लॉक" are kept as transliterations rather than coined Marathi terms,
 *     which matches common usage in Maharashtra municipal GIS work but should
 *     be confirmed against this municipality's own register.
 *   - "GeoJSON" / "Shapefile" / "COG" / file extensions are left untranslated
 *     throughout (common convention in Marathi technical/government IT usage
 *     for file-format names).
 *   - A few short idiomatic UI phrases (e.g. "partial", the Highlight
 *     progress legend) were translated for sense rather than word-for-word;
 *     flagged again at their definition below.
 */
export const mr: Translations = {
  common: {
    cancel: "रद्द करा",
    delete: "हटवा",
    deleting: "हटवत आहे…",
    loading: "लोड होत आहे",
    signOut: "साइन आउट",
    dismiss: "बंद करा",
    retry: "पुन्हा प्रयत्न करा",
    save: "जतन करा",
    saving: "जतन करत आहे…",
    add: "जोडा",
    copy: "कॉपी करा",
    copied: "कॉपी केले",
    preparing: "तयार करत आहे…",
    exportAgain: "पुन्हा एक्सपोर्ट करा",
    exportShapefile: "शेपफाईल एक्सपोर्ट करा",
    undo: "पूर्ववत करा",
    discard: "टाकून द्या",
    done: "पूर्ण झाले",
    edit: "संपादित करा",
  },

  status: {
    block: {
      pending: "सुरू झाले नाही",
      in_progress: "प्रगतीपथावर",
      submitted: "सादर केले",
    },
    raster: {
      receiving: "अपलोड होत आहे",
      uploaded: "रांगेत",
      processing: "रूपांतरित होत आहे",
      ready: "तयार",
      failed: "अयशस्वी",
    },
  },

  ui: {
    hidePassword: "पासवर्ड लपवा",
    showPassword: "पासवर्ड दाखवा",
    surveyProgress: "सर्वेक्षण प्रगती",
  },

  topBar: {
    signOut: "साइन आउट",
    language: "भाषा",
    breadcrumb: "ब्रेडक्रम्ब",
  },

  sidebar: {
    brandSubtitle: "सर्वेक्षण कामकाज",
    workspace: "कार्यक्षेत्र",
    // TODO(mr-review): झोन/प्रभाग/ब्लॉक — see file-top note.
    projects: "प्रकल्प",
    administration: "प्रशासन",
    accounts: "खाती",
    sessions: "फोन व सत्रे",
    reopenRequests: "पुन्हा उघडण्याच्या विनंत्या",
    systemAccess: "प्रणाली प्रवेश",
    signedIn: "साइन इन केले आहे",
    expandMenu: "मेनू उघडा",
    collapseMenu: "मेनू बंद करा",
  },

  appShell: {
    checkingSession: "सत्र तपासत आहे…",
  },

  login: {
    brandSubtitle: "सर्वेक्षण कामकाज",
    title: "साइन इन करा",
    subtitle: "कार्यालयीन आणि क्षेत्रीय दोन्ही प्रकारची खाती येथूनच साइन इन करतात.",
    username: "वापरकर्तानाव",
    password: "पासवर्ड",
    signingIn: "साइन इन होत आहे…",
    signIn: "साइन इन करा",
    cantReachServer: "सर्व्हरशी संपर्क होऊ शकत नाही. बॅकएंड सुरू आहे का ते तपासा.",
    signInFailed: "साइन इन अयशस्वी",
    signedOutReason: "तुम्ही साइन आउट झालात:",
  },

  projectsPage: {
    heading: "प्रकल्प",
    subheading: "प्रत्येक ऑर्थोमोझॅक आणि क्षेत्रात संकलित केलेला आकार एका प्रकल्पाशी संबंधित असतो.",
    // TODO(mr-review): Zone/Ward/Survey Block terminology — see file-top note.
    zones: "झोन",
    wards: "प्रभाग",
    blocks: "ब्लॉक",
    complete: "पूर्ण",
    submittedOfTotal: (submitted: number, total: number) => `${total} पैकी ${submitted} सादर`,
    allProjects: (count: number) => `सर्व प्रकल्प · ${count}`,
    filterPlaceholder: "नावाने फिल्टर करा…",
    loadingProjects: "प्रकल्प लोड होत आहेत…",
    noProjectsYet: "अद्याप प्रकल्प नाहीत",
    noMatches: "जुळणी नाही",
    noProjectsBody:
      "ड्रोन इमेजरी अपलोड करणे आणि सर्वेक्षण क्षेत्र निश्चित करणे सुरू करण्यासाठी तुमचा पहिला प्रकल्प तयार करा.",
    noMatchesBody: "त्या फिल्टरशी जुळणारा कोणताही प्रकल्प नाही.",
    colProject: "प्रकल्प",
    colProgress: "प्रगती",
    colDescription: "वर्णन",
    colCreated: "तयार केले",
    colActions: "कृती",
    active: "सक्रिय",
    percentComplete: (pct: number) => `${pct}% पूर्ण`,
    zeroBlocks: "0 ब्लॉक",
    open: "उघडा",
    zonesAction: "झोन",
    deleteProjectTitle: "हा प्रकल्प हटवायचा?",
    deleteProjectBodySuffix:
      " आणि त्याखालील सर्व काही — प्रत्येक झोन, प्रभाग आणि ब्लॉक, अपलोड केलेली सर्व इमेजरी, आणि सर्वेक्षकांनी संकलित केलेला प्रत्येक आकार — कायमचे हटवले जाईल. हे पोर्टलमधील सर्वात विनाशकारी कृती आहे आणि ती पूर्ववत करता येणार नाही.",
    deleteProjectConfirmLabel: "प्रकल्प हटवा",
    exportedCanDelete: "एक्सपोर्ट झाले. आता तुम्ही हटवू शकता.",
    exportRequired: "हटवण्यापूर्वी एक्सपोर्ट आवश्यक आहे.",
    couldntDeleteProject: "प्रकल्प हटवता आला नाही.",
    couldntReachBackend: "बॅकएंडशी संपर्क होऊ शकला नाही. तो पोर्ट 8300 वर सुरू आहे का?",
    propertyProgress: "मालमत्ता सर्वेक्षण प्रगती",
    propertiesTotal: (n: number) => `${n} मालमत्ता`,
    surveyed: "सर्वेक्षित",
    notStarted: "सुरू न झालेले",
    byWard: "प्रभागनिहाय",
    byBlock: "ब्लॉकनिहाय",
    colWard: "प्रभाग",
    colBlock: "ब्लॉक",
    noProperties: "अद्याप कोणत्याही मालमत्ता आयात किंवा संकलित केलेल्या नाहीत.",
    progressFailed: "मालमत्ता प्रगती लोड करता आली नाही. हा बदल होण्यापूर्वी बॅकएंड सुरू झाला असल्यास तो पुन्हा सुरू करा.",
    loadingProgress: "प्रगती लोड होत आहे…",
    overview: "एकूण आढावा",
    kpiProjects: "प्रकल्प",
    kpiProjectsHint: (active: number) => `${active} मध्ये सर्वेक्षण सुरू`,
    kpiBlocks: "ब्लॉक",
    kpiBlocksHint: (zones: number, wards: number) => `${zones} झोन · ${wards} प्रभाग`,
    kpiSubmitted: "सादर केलेले ब्लॉक",
    kpiSubmittedHint: (pct: string) => `सर्व ब्लॉकपैकी ${pct}`,
    kpiInProgress: "प्रगतीपथावरील ब्लॉक",
    kpiInProgressHint: (n: number) => `${n} सुरू न झालेले`,
    colStatus: "स्थिती",
    colHierarchy: "झोन · प्रभाग · ब्लॉक",
    statusNotStarted: "सुरू न झालेले",
    statusInProgress: "प्रगतीपथावर",
    statusComplete: "पूर्ण",
    statusNoBlocks: "अद्याप ब्लॉक नाहीत",
    legendSubmitted: "सादर",
    legendInProgress: "प्रगतीपथावर",
    legendNotStarted: "सुरू न झालेले",
    blocksProgress: (submitted: number, total: number, pct: number) => `${submitted}/${total} · ${pct}%`,
    moreActions: (name: string) => `${name} साठी अधिक कृती`,
    hierarchyAction: "झोन, प्रभाग व ब्लॉक",
    deleteAction: "प्रकल्प हटवा…",
    deleting: "हटवत आहे…",
    selectedProject: "निवडलेला प्रकल्प",
    openWorkspace: "कार्यक्षेत्र उघडा",
    selectHint: "खाली तपशील पाहण्यासाठी ओळीवर क्लिक करा.",
    sortBy: (col: string) => `${col} नुसार क्रमवारी`,
    searchLabel: "प्रकल्प शोधा",
  },

  hierarchyPage: {
    crumbZonesWardsBlocks: "झोन, प्रभाग आणि ब्लॉक",
    zonesTitle: "झोन",
    wardsTitle: "प्रभाग",
    blocksTitle: "ब्लॉक",
    zonePlaceholder: "उदा. झोन 4",
    wardPlaceholder: "उदा. प्रभाग 12",
    blockPlaceholder: "उदा. ब्लॉक 5",
    selectZoneFirst: "आधी एक झोन निवडा",
    selectWardFirst: "आधी एक प्रभाग निवडा",
    noneYet: "अद्याप काहीही नाही — खाली एक जोडा.",
    noBoundaryGeometry: "सीमा भूमिती नाही",
    noShape: "आकार नाही",
    blocksInWard: "प्रभागातील ब्लॉक",
    submitted: "सादर केले",
    inProgress: "प्रगतीपथावर",
    notStarted: "सुरू झाले नाही",
    completedSurveys: (count: number) => `पूर्ण झालेली सर्वेक्षणे · ${count}`,
    nothingSubmittedYet: "अद्याप काहीही सादर केलेले नाही",
    nothingSubmittedBody:
      "जेव्हा एखादा सर्वेक्षक फील्ड अॅपवरून एखादा ब्लॉक सादर करतो, तेव्हा तो कोणी आणि केव्हा सादर केला यासह येथे दिसतो.",
    colBlock: "ब्लॉक",
    colSubmittedBy: "सादरकर्ता",
    colSubmitted: "सादर केले",
    colClaimedBy: "दावा केलेला",
    colShapes: "आकार",
    claimedBy: (name: string) => `${name} यांनी दावा केला`,
    noBoundaryWarning:
      "सीमा नाही — या ब्लॉकसाठी फील्ड अॅप ऑफलाइन डाउनलोडची व्याप्ती ठरवू शकत नाही",
    submittedCanEdit: "सादर केले — तुम्ही येथे अजूनही संपादित करू शकता",
    surveyLocked: "सर्वेक्षण डेटा लॉक केलेला आहे",
    reopening: "पुन्हा उघडत आहे…",
    sendBackToSurveyor: "सर्वेक्षकाकडे परत पाठवा",
    sendBackTitle:
      "हा ब्लॉक पुन्हा कामासाठी सर्वेक्षकाकडे परत करा. सादर केलेला ब्लॉक त्यांना दिसू शकत नाही.",
    featureTableLockedReason: "हा ब्लॉक सादर केलेला आहे. दुरुस्त्यांसाठी कार्यालयाला विचारा.",
    reopenedNoticeInProgress: (surveyor: string) => `, ${surveyor} यांच्यासह`,
    reopenedNoticePrefix: (blockName: string) => `${blockName} आता सर्वेक्षकाकडे परत आहे (सध्या `,
    reopenedNoticeInProgressState: "प्रगतीपथावर",
    reopenedNoticePendingState: "सुरू झाले नाही — त्यावर कोणताही दावा नव्हता, त्यामुळे कोणीही ते घेऊ शकते",
    reopenedNoticeSuffix: "). पुनर्काम पूर्ण झाल्यावर ते पुन्हा सादर करतील.",
  },

  usersPage: {
    crumbAccounts: "खाती",
    crumbCreation: "निर्मिती",
    crumbManaging: "व्यवस्थापन",
    headingCreate: "खाते निर्मिती",
    headingManage: "खाते व्यवस्थापन",
    subheadingCreate: "सर्वेक्षक आणि कार्यालयीन कर्मचाऱ्यांसाठी ओळखपत्रे तयार करा.",
    subheadingManage: "ओळखपत्रे आणि नेमून दिलेली सर्वेक्षण क्षेत्रे शोधा, तपासा आणि कॉन्फिगर करा.",
    tabManaging: "व्यवस्थापन",
    tabCreation: "निर्मिती",
    searchPlaceholder: "वापरकर्तानाव किंवा नावाने शोधा…",
    roleLabel: "भूमिका:",
    projectLabel: "प्रकल्प:",
    allRoles: "सर्व भूमिका",
    roleSurveyor: "सर्वेक्षक",
    roleStaff: "कर्मचारी",
    roleAdmin: "प्रशासक",
    allProjects: "सर्व प्रकल्प",
    newAccount: "नवीन खाते",
    accountsTitle: (shown: number, total: number | null) =>
      total !== null && shown !== total ? `खाती (${total} पैकी ${shown})` : `खाती (${shown})`,
    accountsTitlePlain: "खाती",
    loadingAccounts: "खाती लोड होत आहेत…",
    noAccountsYet: "अद्याप कोणतेही खाते तयार केलेले नाही. पहिले खाते तयार करण्यासाठी निर्मिती टॅब वापरा.",
    noAccountsMatch: "सध्याच्या शोध किंवा भूमिका फिल्टरशी कोणतेही खाते जुळत नाही.",
    you: "(तुम्ही)",
    usernameLabel: "वापरकर्तानाव:",
    password: "पासवर्ड",
    progress: "प्रगती",
    assignment: "नेमणूक",
    allAccessAdmin: "सर्व प्रवेश (प्रशासक)",
    delete: "हटवा",
    assignSurveyAreasTitle: "सर्वेक्षण क्षेत्रे नेमून द्या",
    assignSurveyAreasDescriptionPrefix: "यासाठी प्रकल्प, झोन, प्रभाग आणि ब्लॉक अधिकार कॉन्फिगर करा",
    loadingGrants: "विद्यमान अधिकार आणि सर्वेक्षण संरचना लोड होत आहे…",
    changePasswordTitle: "पासवर्ड बदला",
    changePasswordDescriptionPrefix: "यासाठी नवीन पासवर्ड सेट करा",
    deleteAccountTitle: "हे खाते हटवायचे?",
    deleteAccountBodyPrefix: "हटवायचे",
    deleteAccountBodySuffix:
      "? ते यापुढे फील्ड अॅप किंवा पोर्टलमध्ये लॉग इन करू शकणार नाहीत. त्यांनी यापूर्वी सादर केलेले सर्वेक्षण आकार जतन राहतील.",
    deleteAccountConfirmLabel: "खाते हटवा",
    newAccountPanelTitle: "नवीन खाते",
    fieldUsernameLabel: "वापरकर्तानाव",
    fieldUsernameHint: "साइन इन करण्यासाठी वापरले जाते. लहान अक्षरे, जागा नकोत.",
    fieldFullNameLabel: "पूर्ण नाव",
    fieldFullNameHint: "ऐच्छिक. संपूर्ण पोर्टलमध्ये दाखवले जाणारे नाव.",
    fieldPasswordLabel: "पासवर्ड",
    fieldPasswordHint: "वापरकर्त्याला थेट द्या; पासवर्ड नंतर पाहता येत नाहीत.",
    passwordPlaceholder: "किमान 8 अक्षरे",
    weakPassword: "किमान 8 अक्षरे वापरा.",
    fieldRoleLabel: "भूमिका",
    fieldRoleHint: "अधिकार पातळी निवडा. सविस्तर तुलना तक्ता उजवीकडे दाखवला आहे.",
    fieldInitialProjectsLabel: "प्रारंभिक प्रकल्प",
    fieldInitialProjectsHint:
      "हे खाते लगेच कोणत्या मोहिमांमध्ये प्रवेश करू शकते. विशिष्ट ब्लॉक नंतर सुधारित करता येतील.",
    creatingAccount: "तयार करत आहे…",
    createAccount: "खाते तयार करा",
    roleMatrixTitle: "प्रत्येक भूमिका काय करू शकते",
    roleMatrixIntro:
      "FastAPI डिपेंडन्सीजद्वारे परवानग्या काटेकोरपणे सर्व्हर-बाजूला लागू केल्या जातात. पोर्टल, मोबाइल अॅप किंवा API द्वारे कोणतीही भूमिका या तक्त्याबाहेरील कृती करू शकत नाही.",
    colCapability: "क्षमता",
    colSurveyor: "सर्वेक्षक",
    colStaff: "कर्मचारी",
    colAdmin: "प्रशासक",
    rolePrinciplesTitle: "भूमिका तत्त्वे",
    rolePrincipleStaffAdminLabel: "कर्मचारी वि. प्रशासक:",
    rolePrincipleStaffAdmin:
      "सर्व GIS कामकाजात समान (इमेजरी अपलोड, व्हेक्टर संपादन, एक्सपोर्ट). प्रशासकांना याव्यतिरिक्त इतर प्रशासक खाती व्यवस्थापित करण्याचा अधिकार आहे.",
    rolePrincipleFieldLabel: "क्षेत्रीय सर्वेक्षणे:",
    rolePrincipleField:
      "सर्वेक्षकांना केवळ त्यांना स्पष्टपणे नेमून दिलेले ब्लॉक दिसतात. एकदा ब्लॉक सादर केल्यावर, कर्मचाऱ्यांनी पुन्हा उघडल्याशिवाय मोबाइलवरून पुढील संपादनांपासून तो संरक्षित राहतो.",
    rolePrincipleFailClosedLabel: "डीफॉल्ट अपयश-बंद:",
    rolePrincipleFailClosed:
      "कोणतीही प्रकल्प नेमणूक नसलेली खाती नेमणूक होईपर्यंत कोणत्याही डेटामध्ये प्रवेश करू शकत नाहीत.",
    loadingProjects: "प्रकल्प लोड होत आहेत…",
    noProjectsYetCreateOne: "अद्याप कोणतेही प्रकल्प अस्तित्वात नाहीत. आधी एक तयार करा, मग येथे नेमून द्या.",
    selectedOfTotal: (selected: number, total: number) => `${total} पैकी ${selected} निवडले`,
    clearAll: "सर्व साफ करा",
    selectAll: "सर्व निवडा",
    everythingAdmin: "सर्वकाही",
    adminSuffix: "(प्रशासक)",
    nothingAssigned: "काहीही नेमून दिलेले नाही — या खात्याला काहीही दिसू शकत नाही",
    setPassword: "पासवर्ड सेट करा",
    newPasswordPlaceholder: "नवीन पासवर्ड",
    createdAdminNotice: (username: string) =>
      `${username} तयार केले — सर्व प्रकल्पांमध्ये प्रवेश असलेला प्रशासक.`,
    createdNoAssignmentNotice: (username: string) =>
      `${username} तयार केले. सर्वेक्षण क्षेत्रे नेमून देण्यासाठी व्यवस्थापनमधील "नेमणूक" वापरा.`,
    createdWithAssignmentsNotice: (username: string, count: number) =>
      `${username} तयार केले, ${count} प्रकल्प नेमणुकांसह.`,
    usernameAlreadyTaken: "ते वापरकर्तानाव आधीच घेतले गेले आहे.",
    onlyStaffCanCreate: "फक्त कर्मचारी खातीच नवीन खाती तयार करू शकतात.",
    deletedAccountNotice: (username: string) => `खाते "${username}" हटवले.`,
    passwordUpdatedNotice: (username: string) =>
      `"${username}" साठी पासवर्ड अद्ययावत केला. नवीन पासवर्ड त्यांना थेट द्या.`,
    noAssignmentsNotice: (username: string) =>
      `${username} ला कोणतीही नेमणूक नाही आणि कोणत्याही सर्वेक्षण क्षेत्रात प्रवेश करू शकत नाही.`,
    updatedAssignmentsNotice: (username: string, count: number) =>
      `${username} साठी नेमणुका अद्ययावत केल्या (${count} अधिकार).`,
    zoneCount: (n: number) => `${n} झोन`,
    wardCount: (n: number) => `${n} प्रभाग`,
    blockCount: (n: number) => `${n} ब्लॉक`,
    roleMatrix: {
      seeBlocks: "ब्लॉक पाहणे",
      seeSubmittedData: "सादर केलेला सर्वेक्षण डेटा पाहणे",
      claimBlock: "ब्लॉकवर दावा करणे",
      collectEditShapes: "आकार संकलित करणे / संपादित करणे",
      submitBlock: "ब्लॉक सादर करणे",
      editSubmittedBlock: "सादर केलेल्या ब्लॉकचा डेटा संपादित करणे",
      reopen: "पुन्हा उघडणे (पुनर्कामासाठी परत पाठवणे)",
      exportShapefile: "शेपफाईल एक्सपोर्ट करणे",
      hierarchyManage: "प्रकल्प / झोन / प्रभाग / ब्लॉक",
      imageryManage: "ड्रोन इमेजरी + मालमत्ता आराखडे",
      whichProjects: "कोणते प्रकल्प",
      manageAccounts: "खाती व्यवस्थापित करणे",
      assignProjects: "खात्यांना प्रकल्प नेमून देणे",
      surveyorSeeBlocks: "प्रलंबित + प्रगतीपथावरील",
      staffSeeBlocks: "सर्व, सादर केलेल्यांसह",
      adminSeeBlocks: "सर्व, सादर केलेल्यांसह",
      surveyorSeeSubmitted: "नाही",
      staffSeeSubmitted: "होय",
      adminSeeSubmitted: "होय",
      surveyorClaim: "केवळ दावा न केलेले",
      staffClaim: "दावा केलेले फक्त वाचनीय स्वरूपात उघडते",
      adminClaim: "दावा केलेले फक्त वाचनीय स्वरूपात उघडते",
      surveyorCollect: "त्यांच्याकडील ब्लॉकमध्ये",
      staffCollect: "होय",
      adminCollect: "होय",
      surveyorSubmit: "स्वतः दावा केलेला ब्लॉक",
      staffSubmit: "कोणताही ब्लॉक",
      adminSubmit: "कोणताही ब्लॉक",
      surveyorEditSubmitted: "नाही",
      staffEditSubmitted: "होय, जागेवरच",
      adminEditSubmitted: "होय, जागेवरच",
      surveyorReopen: "नाही",
      staffReopen: "होय",
      adminReopen: "होय",
      surveyorExport: "फक्त एकच ब्लॉक",
      staffExport: "संपूर्ण प्रकल्प",
      adminExport: "संपूर्ण प्रकल्प",
      surveyorHierarchy: "फक्त वाचनीय",
      staffHierarchy: "तयार करणे + हटवणे",
      adminHierarchy: "तयार करणे + हटवणे",
      surveyorImagery: "प्रवेश नाही",
      staffImagery: "अपलोड, नोंदणी, हटवणे",
      adminImagery: "अपलोड, नोंदणी, हटवणे",
      surveyorWhichProjects: "फक्त नेमून दिलेलेच",
      staffWhichProjects: "फक्त नेमून दिलेलेच",
      adminWhichProjects: "सर्व, नेहमी",
      surveyorManageAccounts: "नाही",
      staffManageAccounts: "सर्वेक्षक + कर्मचारी",
      adminManageAccounts: "कोणीही, इतर प्रशासकांसह",
      surveyorAssignProjects: "नाही",
      staffAssignProjects: "होय",
      adminAssignProjects: "होय",
    },
  },

  projectWorkspacePage: {
    imageryAndFieldData: "प्रकल्प-स्तरीय इमेजरी आणि क्षेत्रीय डेटा.",
    imageryReadyOfTotal: (ready: number, total: number) => `${total} पैकी ${ready} इमेजरी फाईल`,
    // Marathi doesn't need an English-style plural suffix on the noun; the
    // count argument is accepted for signature parity with en.ts but unused.
    imageryReadySuffix: () => " तयार.",
    zonesWardsBlocks: "झोन, प्रभाग आणि ब्लॉक",
    surveyTiming: "सर्वेक्षण वेळ",
    hideUploadPanels: "अपलोड पॅनेल लपवा",
    showUploadPanels: "अपलोड पॅनेल दाखवा",
    droneImagery: "ड्रोन इमेजरी",
    propertyOutlines: "मालमत्ता आराखडे",
    editableLayers: "संपादनयोग्य स्तर",
    exportScopeNote: "एक्सपोर्टमध्ये संपूर्ण प्रकल्पाचा समावेश आहे, या फिल्टरचा नाही.",
    qaChecking: "मालमत्तांमध्ये ओव्हरलॅप व स्लिव्हर तपासत आहे…",
    qaNone: (checked: number) => `${checked} मालमत्तांमध्ये कोणताही ओव्हरलॅप किंवा स्लिव्हर आढळला नाही.`,
    qaOverlaps: "ओव्हरलॅप",
    qaSlivers: "स्लिव्हर",
    qaCheckedOf: (checked: string) => `${checked} तपासलेल्या मालमत्तांमध्ये`,
    qaOverlap: "ओव्हरलॅप",
    qaSliver: "स्लिव्हर",
    qaPercentOfSmaller: (pct: number) => `लहान मालमत्तेच्या ${pct}%`,
    qaVeryThin: "खूप अरुंद",
    qaVerySmall: "खूप लहान",
    qaFailed: "ओव्हरलॅप व स्लिव्हर तपासणी चालवता आली नाही.",
    qaShowList: "यादी दाखवा",
    qaHideList: "यादी लपवा",
    qaListCapped: (shown: number) => `पहिले ${shown} दाखवत आहे.`,
    qaZoom: "झूम",
    qaClose: "तपासणी बंद करा",
  },

  hierarchyImportPanel: {
    panelTitle: "GeoJSON मधून आयात करा",
    intro:
      "या प्रकल्पाची संरचना तयार करण्यासाठी प्रत्येक स्तरासाठी एक GeoJSON अपलोड करा. प्रभाग आणि ब्लॉक ते ज्या झोन किंवा प्रभागात असतील त्याला जोडले जातात, त्यामुळे फाईल्समध्ये जुळणाऱ्या ID स्तंभांची गरज नाही. प्रक्षेपित फाईल (उदा. UTM) तिचा CRS नमूद केल्यास आपोआप रूपांतरित होते.",
    levelZone: "झोन",
    levelWard: "प्रभाग",
    levelBlock: "ब्लॉक",
    hintZone: "आधी आयात करा",
    hintWard: "झोनमध्ये ठेवले",
    hintBlock: "प्रभागांमध्ये ठेवले",
    chooseFile: "फाईल निवडा",
    importing: "आयात करत आहे…",
    removing: "काढत आहे…",
    removeAll: (count: number, label: string) => `सर्व ${count} ${label} काढा`,
    uploadsTitle: "अपलोड",
    loadingUploads: "लोड होत आहे…",
    noUploadsRecorded:
      "अद्याप कोणतेही अपलोड नोंदवलेले नाहीत. इथे आयात केलेल्या फाईलसाठी, आणि तुम्ही हे पान सोडेपर्यंतच, पूर्ववत करा उपलब्ध आहे — त्यानंतर, आयात केलेला स्तर काढण्यासाठी वरील “सर्व काढा” वापरावे लागेल.",
    undoOfferNote: "तुम्ही आत्ताच आयात केलेल्यासाठी पूर्ववत करा उपलब्ध आहे, आणि तुम्ही हे पान सोडेपर्यंतच.",
    onlyGeojsonAccepted: "फक्त .geojson / .json फाईल्स स्वीकारल्या जातात.",
    couldntImport: (level: string) => `${level} आयात करता आले नाहीत.`,
    couldntRemove: (label: string) => `${label} काढता आले नाहीत.`,
    serverStillRefused: "सर्व्हरने काढण्यास पुन्हा नकार दिला.",
    removedNothingAlreadyEmpty: "काहीही नाही — आधीच रिकामे",
    removedSummary: (parts: string) => `काढले: ${parts}`,
    zonesCount: (n: number) => `${n} झोन`,
    wardsCount: (n: number) => `${n} प्रभाग`,
    blocksCount: (n: number) => `${n} ब्लॉक`,
    exportFailedGeneric: "एक्सपोर्ट अयशस्वी.",
    undoThisUpload: (name: string) => `${name} पूर्ववत करायचे?`,
    thisUpload: "हे अपलोड",
    removeAllQuestion: (label: string) => `सर्व ${label} काढायचे?`,
    undoBodyTemplate: (count: number, level: string) =>
      `हे अपलोड या ${count} ${level}(स) तयार केले होते, ती काढली जातील, इतर सर्व अपलोड जसेच्या तसे राहतील.`,
    removeBodyTemplate: (label: string) => `या प्रकल्पातील आयात केलेले सर्व ${label} काढले जातील.`,
    cascadeZone: " त्यासोबत प्रत्येक प्रभाग आणि ब्लॉकही जाईल.",
    cascadeWard: " त्यासोबत प्रत्येक ब्लॉकही जाईल.",
    reimportToRestore: " ते परत मिळवण्यासाठी तुम्हाला फाईल पुन्हा आयात करावी लागेल.",
    undoImportLabel: "आयात पूर्ववत करा",
    removeLabel: "काढा",
    destroysFieldWorkTitle: "यामुळे संकलित केलेले क्षेत्रीय काम नष्ट होईल",
    destroysFieldWorkBody1: "यामुळे कायमचे हटवले जाईल",
    destroysFieldWorkBody2: "संकलित आकार",
    destroysFieldWorkBody3: "एकूण",
    destroysFieldWorkBody4: "ब्लॉकमध्ये",
    destroysFieldWorkBody5: "आधीच सुरू झालेले किंवा सादर केलेले",
    destroysFieldWorkImagery: (count: number) => `, तसेच ${count} इमेजरी फाईल(ली)`,
    destroysFieldWorkBody6:
      ". सर्वेक्षकाने जागेवर जाऊन काढलेला आकार कार्यालयाकडे असलेल्या कशावरूनही पुन्हा तयार करता येत नाही — आधी एक्सपोर्ट करा.",
    deleteAnyway: "तरीही हटवा",
    exportedCanRemove: "एक्सपोर्ट झाले. आता तुम्ही काढू शकता.",
    exportFieldDataFirst: "आधी क्षेत्रीय डेटा एक्सपोर्ट करा.",
    exportedCanDelete: "एक्सपोर्ट झाले. आता तुम्ही हटवू शकता.",
    exportRequiredDelete: "हटवण्यापूर्वी एक्सपोर्ट आवश्यक आहे.",
    exportNoteWholeProject:
      "या स्तराखालील आकारच नव्हे तर या प्रकल्पातील प्रत्येक आकार डाउनलोड होतो — काढण्याची कृती प्रकल्पाचे ब्लॉक कोणत्याही परिस्थितीत साफ करते.",
    alreadyHasTitle: (existing: number, label: string) => `या प्रकल्पात आधीच ${existing} ${label} आहेत`,
    importAddsPrefix: "आयात करत आहे",
    importAddsAddsTo: "यात भर घालते",
    importAddsSuffix:
      "त्यांची जागा घेण्याऐवजी — काहीही जुळवले किंवा अधिलिखित केले जात नाही, त्यामुळे तीच फाईल पुन्हा आयात केल्यास प्रत्येक पंक्तीची, नावांसह, एक डुप्लिकेट तयार होते. त्याऐवजी बदलण्यासाठी, आधी विद्यमान",
    importAddsFirst: "काढा.",
    importAnyway: "तरीही आयात करा",
    createdCount: (created: number, level: string) => `${created} ${level} तयार केले`,
    skippedCount: (n: number) => `${n} वगळले`,
    placedByFallback: (n: number) => `${n} फॉलबॅकने ठेवले`,
    reprojectedFromCrs: (crs: string) => `${crs} वरून रूपांतरित`,
  },

  shapeUpdate: {
    title: "आकार दुरुस्त करा (फील्ड डेटा सुरक्षित)",
    intro:
      "स्तर त्याच्या id सह निर्यात करा, GIS मध्ये आकार, नावे किंवा पालक (zone_id / ward_id) बदला, नंतर बदललेल्या फाइलमधून अद्यतन करा. ओळी gs_id ने किंवा पालक + नावाने जुळवल्या जातात; काहीही तयार किंवा हटवले जात नाही.",
    exportLevel: (label: string) => `${label} निर्यात`,
    updateLevel: (label: string) => `${label} अद्यतन…`,
    exporting: "निर्यात होत आहे…",
    checking: "फाइल तपासत आहे…",
    previewTitle: (label: string, file: string) => `${file} मधून ${label} अद्यतनित करायचे?`,
    summary: (r: { reshaped: number; renamed: number; moved: number; unchanged: number; skipped: number; untouched: number }) =>
      `${r.reshaped} आकार बदल · ${r.renamed} नाव बदल · ${r.moved} हलवले · ${r.unchanged} न बदललेले · ${r.skipped} वगळले · ${r.untouched} फाइलमध्ये नाहीत (तसेच राहतील)`,
    nothingToApply: "या फाइलमध्ये साठवलेल्यापेक्षा काहीही वेगळे नाही.",
    outsideWarning: (n: number) =>
      `${n} फील्ड-डेटा आकार त्यांच्या ब्लॉकच्या नवीन सीमेबाहेर आहेत आणि कोणताही दुसरा ब्लॉक त्यांना व्यापत नाही. ते त्याच ब्लॉकमध्ये राहतात, पण सीमा त्यांना व्यापेपर्यंत सर्वेक्षक ते संपादित करू शकत नाहीत.`,
    movedInfo: (n: number) =>
      `${n} मालमत्ता त्यांना आता सामावणाऱ्या ब्लॉकमध्ये हलवल्या जातील (प्रभाग व झोन त्यानुसार). पूर्ववत केल्यास त्या परत जातात.`,
    movedRow: (house: string | null, from: string, to: string) =>
      `${house ? `क्र. ${house}` : "क्रमांक नाही"}: ${from} → ${to}`,
    toSubmitted: "सबमिट केलेला ब्लॉक",
    colName: "नाव",
    colMatchedBy: "जुळवले",
    colChange: "बदल",
    changeShape: "आकार",
    changeName: (from: string) => `नाव (पूर्वी ${from})`,
    changeMove: (from: string | null, to: string | null) => `हलवले ${from ?? "?"} → ${to ?? "?"}`,
    outsideCount: (n: number) => `${n} बाहेर`,
    skippedTitle: "वगळले",
    skippedRow: (n: number, name: string | null, reason: string) => `वैशिष्ट्य ${n}${name ? ` (${name})` : ""}: ${reason}`,
    listCapped: "फक्त पहिले 300 दाखवले आहेत.",
    apply: (n: number) => `${n} ओळी अद्यतनित करा`,
    applied: (n: number, label: string) => `${label}: ${n} अद्यतनित.`,
    historyTitle: "आकार अद्यतने",
    historyRow: (r: { reshaped: number; renamed: number; moved: number }) =>
      `${r.reshaped} आकार बदल · ${r.renamed} नाव बदल · ${r.moved} हलवले`,
    undone: "पूर्ववत केले",
    undo: "पूर्ववत",
    undoTitle: "हे आकार अद्यतन पूर्ववत करायचे?",
    undoBody: "या अद्यतनापूर्वीचे आकार, नावे आणि पालक परत येतात, आणि परत हलवलेल्या ब्लॉक/प्रभागानुसार फील्ड डेटा जुळवला जातो. फील्ड डेटा स्वतः बदलत नाही.",
    failed: "आकार अद्यतनित करता आले नाहीत.",
  },

  propertyImportPanel: {
    panelTitle: (total: number) => (total ? `मालमत्ता आराखडे · ${total}` : "मालमत्ता आराखडे"),
    dropHint: "एक GeoJSON टाका, किंवा ब्राउझ करण्यासाठी क्लिक करा",
    importingFile: (name: string) => `${name} आयात करत आहे…`,
    reuploadHint: "पुन्हा अपलोड केल्यास फक्त नवीन असलेलेच जोडले जाते",
    onlyGeojsonAccepted: "फक्त .geojson / .json फाईल्स स्वीकारल्या जातात.",
    importFailed: "आयात अयशस्वी.",
    couldntUndoUpload: "ते अपलोड पूर्ववत करता आले नाही.",
    couldntDeleteOutlines: "आराखडे हटवता आले नाहीत.",
    serverRefusedDelete: "सर्व्हरने आराखडे हटवण्यास नकार दिला.",
    createdAdded: (count: number) => `${count} जोडले`,
    alreadyPresent: (count: number) => `, ${count} आधीच अस्तित्वात`,
    outsideEveryBlock: (count: number) => `, ${count} प्रत्येक ब्लॉकच्या बाहेर`,
    everythingAlreadyImported: "या फाईलमधील सर्व काही आधीच आयात केलेले होते — काहीही बदलले नाही.",
    reprojectedFrom: (crs: string) => `${crs} वरून रूपांतरित.`,
    unplacedHint:
      "प्रत्येक ब्लॉकच्या बाहेरील आराखडे सर्वेक्षण करता येत नाहीत — त्यांना व्यापणारे ब्लॉक आयात करा, मग ही फाईल पुन्हा अपलोड करा.",
    numberedInField: "क्षेत्रात क्रमांकित",
    numberedOfTotal: (done: number, total: number) => `${total} पैकी ${done}`,
    propertiesNumberedLabel: "क्रमांकित मालमत्ता",
    loadingUploads: "लोड होत आहे…",
    noOutlinesYetTitle: "अद्याप मालमत्ता आराखडे नाहीत",
    noOutlinesYetBody:
      "इमारतींच्या आराखड्यांचे GeoJSON अपलोड करा. ते सर्वेक्षकाच्या नकाशावर क्रमांकित करण्यास तयार दिसतात, त्यामुळे हाताने काहीही रेखाटावे लागत नाही.",
    noUploadsRecordedFor: (total: number) =>
      `या ${total} आराखड्यांसाठी कोणतेही अपलोड नोंदवलेले नाहीत. ते या सुविधेपूर्वीचे असू शकतात, किंवा आयात न करता थेट क्षेत्रात तयार केले गेले असतील.`,
    uploadsTitle: "अपलोड",
    propertyImportFallback: "मालमत्ता आयात",
    remainingRowsTitle: "या अपलोडमधील अद्याप अस्तित्वात असलेल्या पंक्ती",
    undoOfferNote:
      "तुम्ही आत्ताच अपलोड केलेल्यासाठी पूर्ववत करा उपलब्ध आहे, आणि तुम्ही हे पान सोडेपर्यंतच. त्यानंतर, सर्व आराखडे हटवा वापरा.",
    deleteAllOutlines: "सर्व आराखडे हटवा",
    exportedCanVerb: (verb: string) => `एक्सपोर्ट झाले. आता तुम्ही ${verb} शकता.`,
    exportFieldDataFirst: "आधी क्षेत्रीय डेटा एक्सपोर्ट करा.",
    verbUndo: "पूर्ववत करू",
    verbDelete: "हटवू",
    verbContinue: "पुढे जाऊ",
    undoThisUpload: (name: string) => `${name} पूर्ववत करायचे?`,
    thisUpload: "हे अपलोड",
    undoBody: (count: number) =>
      `हे अपलोड या ${count} मालमत्ता आराखड्या(ड्यां) तयार केल्या होत्या, त्या काढल्या जातील, इतर सर्व अपलोड जसेच्या तसे राहतील. त्या परत मिळवण्यासाठी तुम्हाला फाईल पुन्हा अपलोड करावी लागेल.`,
    undoImportLabel: "आयात पूर्ववत करा",
    destroysFieldWorkTitle: "यामुळे संकलित केलेले क्षेत्रीय काम नष्ट होईल",
    destroysFieldWorkBody: (count: number) => `यापैकी ${count} मालमत्तांना`,
    destroysFieldWorkBodySuffix:
      "सर्वेक्षकाने आधीच मालमत्ता क्रमांक दिलेला आहे. अपलोड पूर्ववत केल्यास ते हटवले जातील — आराखडा पुन्हा आयात करता येतो, क्रमांक करता येत नाही.",
    deleteAnyway: "तरीही हटवा",
    deleteAllQuestion: (total: number) => `सर्व ${total} मालमत्ता आराखडे हटवायचे?`,
    deleteAllBodyPrefix:
      "या प्रकल्पात आयात केलेला प्रत्येक आराखडा, प्रत्येक अपलोडमधून, काढला जाईल — यात सर्वेक्षकांनी आधीच क्रमांकित केलेल्या",
    deleteAllBodySuffix: "यांचाही समावेश आहे. क्षेत्रात हाताने काढलेल्या आकारांना धक्का लागणार नाही.",
    continueLabel: "पुढे चला",
    lastChanceTitle: "शेवटची संधी — हे पूर्ववत करता येणार नाही",
    permanentlyDeleting: () => `कायमचे हटवत आहे`,
    outlineCount: (total: number) => `${total} आराखडा(डे)`,
    ofWhich: ", ज्यापैकी",
    carryHouseNumber:
      "सर्वेक्षकाने क्षेत्रात दिलेला घर क्रमांक आहे. फाईल पुन्हा अपलोड केल्यास आराखडे परत येतील, पण रिकामे — ते क्रमांक कार्यालयाकडे असलेल्या कशावरूनही परत मिळवता येत नाहीत.",
    noneNumberedYet: ". अद्याप कोणालाही क्रमांक दिलेला नाही, त्यामुळे कोणतेही क्षेत्रीय काम गमावले जात नाही.",
    deletePermanently: "कायमचे हटवा",
  },

  rasterPanel: {
    panelTitle: (count: number) => (count ? `ड्रोन इमेजरी · ${count}` : "ड्रोन इमेजरी"),
    dropHint: "एक GeoTIFF येथे टाका, किंवा ब्राउझ करण्यासाठी क्लिक करा",
    uploadingFile: (name: string) => `${name} अपलोड करत आहे…`,
    transferComplete: "स्थानांतर पूर्ण — सर्व्हरवर जतन करत आहे…",
    percentUploaded: (pct: number) => `${pct}% अपलोड झाले`,
    convertedHint: "अपलोडनंतर आपोआप क्लाउड-ऑप्टिमाइझ्ड GeoTIFF मध्ये रूपांतरित केले जाते",
    onlyTiffAccepted: "फक्त .tif / .tiff फाईल्स स्वीकारल्या जातात.",
    uploadFailed: "अपलोड अयशस्वी.",
    couldntAddFile: "ही फाईल जोडता आली नाही.",
    deleteFailed: "हटवणे अयशस्वी.",
    retryFailed: "पुन्हा प्रयत्न अयशस्वी.",
    couldntReadLocation: "TiTiler कडून या इमेजरीचे स्थान वाचता आले नाही.",
    // TODO(mr-review): "_cog.tif" is a file-name/format token — left untranslated.
    cogTifArrow: "_cog.tif →",
    copyFolderPath: "हा फोल्डर मार्ग कॉपी करा",
    foundOnServer: "सर्व्हरवर आढळले · जोडल्यास रूपांतरण वगळले जाते",
    unusable: "वापरण्यायोग्य नाही",
    fileMissing: "फाईल गहाळ",
    fileMissingBody:
      "फाईल यापुढे सर्व्हरवर नाही — ती हटवली गेली किंवा पोर्टलबाहेर हलवली गेली. ही नोंद हटवा, मग फाईल पुन्हा अपलोड करा किंवा पुन्हा जोडा.",
    blockSpecific: "ब्लॉक-विशिष्ट",
    projectWide: "संपूर्ण प्रकल्प",
    approxConverted: (pct: number) => `~${pct}% रूपांतरित`,
    converting: "रूपांतरित होत आहे…",
    noImageryYetTitle: "अद्याप इमेजरी नाही",
    noImageryYetBody: "या प्रकल्पासाठी एक ऑर्थोमोझॅक अपलोड करा आणि ती त्याखालील प्रत्येक ब्लॉकसाठी उपलब्ध होईल.",
    zoomTo: "झूम करा",
    retry: "पुन्हा प्रयत्न करा",
    deleteThisImageryTitle: "ही इमेजरी हटवायची?",
    deleteImageryBodySuffix:
      "आणि तिच्या रूपांतरित टाइल्स सर्व्हरवरून काढल्या जातील. हे पूर्ववत करण्यासाठी मूळ फाईल पुन्हा अपलोड करावी लागेल आणि पुन्हा रूपांतरणाची वाट पाहावी लागेल — अनेकदा अनेक GB आणि अनेक मिनिटे. ब्लॉक आणि संकलित आकारांवर परिणाम होणार नाही.",
    deleteImageryConfirmLabel: "इमेजरी हटवा",
  },

  layerRowsTable: {
    badge: "संपादनयोग्य स्तर",
    badgeTitle: (name: string) =>
      `“${name}” या संपादनयोग्य स्तरातील आकार: संदर्भ माहिती, सर्वेक्षित मालमत्ता नाही. येथे फक्त वाचनासाठी.`,
    layerShapes: (n: string) => `${n} स्तर आकार`,
    loadFailed: "संपादनयोग्य स्तराचे आकार लोड करता आले नाहीत.",
  },
  featureTable: {
    panelTitle: (count: number) => (count ? `क्षेत्रीय डेटा · ${count}` : "क्षेत्रीय डेटा"),
    rowsEdited: (count: number) => `${count} पंक्ती संपादित`,
    discard: "टाकून द्या",
    saving: "जतन करत आहे…",
    save: "जतन करा",
    saveOrDiscardFirst: "आधी तुमचे बदल जतन करा किंवा टाकून द्या",
    done: "पूर्ण झाले",
    edit: "संपादित करा",
    preparing: "तयार करत आहे…",
    exportShapefile: "शेपफाईल एक्सपोर्ट करा",
    noShapesYetTitle: "अद्याप कोणतेही आकार संकलित केलेले नाहीत",
    noShapesYetBody: "सर्वेक्षकांनी क्षेत्रात नोंदवलेले बिंदू आणि इमारतींचे आराखडे समक्रमित होताच येथे दिसतील.",
    colType: "प्रकार",
    colZone: "झोन",
    colWard: "प्रभाग",
    colBlock: "ब्लॉक",
    colPropertyNo: "मालमत्ता क्र.",
    searchPlaceholder: "मालमत्ता क्र. शोधा",
    clearSearch: "शोध साफ करा",
    noMatchTitle: "जुळणारी मालमत्ता नाही",
    noMatchBody: (column: string, q: string) => `या दृश्यात ${column} मध्ये "${q}" शी जुळणारी मालमत्ता आढळली नाही.`,
    tabFieldData: "फील्ड डेटा",
    tabLayers: "संपादनयोग्य स्तर",
    tabAll: "सर्व",
    noMatchAnyBody: (q: string) => `या दृश्यात "${q}" शी काहीही जुळत नाही.`,
    finishEditingFirst: "आधी संपादन पूर्ण करा",
    searchColumnTitle: "शोधायचा स्तंभ",
    searchIn: (column: string) => `${column} शोधा`,
    allColumns: "सर्व स्तंभ",
    colCreatedBy: "तयार करणारा",
    noLayerMatchTitle: "जुळणारा स्तर आकार नाही",
    noLayerMatchBody: "या दृश्यात या फिल्टरशी जुळणारा कोणताही संपादनयोग्य स्तर आकार नाही.",
    noLayerShapesTitle: "येथे स्तर आकार नाहीत",
    noLayerShapesBody: "या क्षेत्रात कोणताही संपादनयोग्य स्तर आकार नाही.",
    allSurveyors: "सर्व सर्वेक्षक",
    noSurveyorMatchTitle: "या सर्वेक्षकाचे काहीही नाही",
    noSurveyorMatchBody: (name: string) => `या दृश्यात ${name} यांनी काहीही गोळा केलेले नाही आणि कोणताही स्तर आकार बदललेला नाही.`,
    surveyorFilterTitle: "या सर्वेक्षकाने गोळा केलेल्या मालमत्ता; संपादनयोग्य स्तरातील त्यांनी शेवटचे बदललेले आकार",
    dateFrom: "पासून",
    dateTo: "पर्यंत",
    clearDates: "तारखा साफ करा",
    dateFilterTitle: "सर्वेक्षण मालमत्ता तयार केलेल्या तारखेनुसार; संपादनयोग्य स्तरातील आकार शेवटच्या बदलाच्या तारखेनुसार",
    noDateMatchTitle: "या तारखांमध्ये काहीही नाही",
    noDateMatchBody: "या दृश्यात या तारखांदरम्यान कोणतीही मालमत्ता तयार झाली नाही आणि कोणताही स्तर आकार बदलला नाही.",
    colMarkRevisit: "पुनर्भेट चिन्हांकित करा",
    markRevisitTitle: "ही मालमत्ता पुनर्भेटीसाठी चिन्हांकित करा (नकाशावर पांढरी दिसेल)",
    revisitOnly: "पुनर्भेटीसाठी चिन्हांकित",
    revisitOnlyTitle: "फक्त पुनर्भेटीसाठी चिन्हांकित मालमत्ता दाखवा",
    noRevisitTitle: "पुनर्भेटीसाठी कोणतीही मालमत्ता चिन्हांकित नाही",
    noRevisitBody: "या यादीत जोडण्यासाठी ओळीवरील \"पुनर्भेट चिन्हांकित करा\" वर खूण करा.",
    revisitFailed: "पुनर्भेट चिन्ह बदलता आले नाही.",
    colPartition: "विभाजन",
    colPlotNo: "प्लॉट क्र.",
    colRecorded: "नोंदवले",
    colActions: "कृती",
    propertyNumberAriaLabel: (id: number) => `मालमत्ता क्रमांक, फिचर ${id}`,
    partitionAriaLabel: (id: number) => `विभाजन, फिचर ${id}`,
    plotNumberAriaLabel: (id: number) => `प्लॉट क्रमांक, फिचर ${id}`,
    columnAriaLabel: (col: string, id: number) => `${col}, फिचर ${id}`,
    zoomToShapeTitle: "नकाशावर या आकारावर झूम करा",
    noGeometryTitle: "या पंक्तीत झूम करण्यासाठी भूमिती नाही",
    zoomToAriaLabel: (label: string) => `${label} वर झूम करा`,
    thisShape: "हा आकार",
    removing: "काढत आहे…",
    delete: "हटवा",
    deleteThisShapeTitle: "हा आकार हटवायचा?",
    propertyNumberPrefix: "मालमत्ता क्रमांक",
    thisGeometry: (type: string) => `हा ${type} `,
    thisShapeFallback: "आकार",
    deleteShapeBodySuffix:
      "क्षेत्रात संकलित केला गेला होता आणि तो परत मिळवता येणार नाही — कोणाला तरी त्या मालमत्तेवर पुन्हा जाऊन तो नोंदवावा लागेल.",
    deleteShapeConfirmLabel: "आकार हटवा",
    savedThenStopped: (saved: number, detail: string) => `${saved} पंक्ती जतन केल्या, नंतर थांबले: ${detail}`,
    couldntSaveChanges: "ते बदल जतन करता आले नाहीत.",
    couldntDeleteShape: "तो आकार हटवता आला नाही.",
    exportFailed: "एक्सपोर्ट अयशस्वी.",
  },

  editableLayersPanel: {
    panelTitle: (count: number) => (count ? `संपादनयोग्य स्तर · ${count}` : "संपादनयोग्य स्तर"),
    dropHint: "एक GeoJSON टाका, किंवा ब्राउझ करण्यासाठी क्लिक करा",
    uploadingFile: (name: string) => `${name} अपलोड करत आहे…`,
    uploadHint: "रस्ते, नाले, आराखडा आच्छादने — फील्ड अॅपमध्ये संपादित केली जातात",
    onlyGeojsonAccepted: "फक्त .geojson / .json फाईल्स स्वीकारल्या जातात.",
    uploadFailed: "अपलोड अयशस्वी.",
    couldntSaveChanges: "ते बदल जतन करता आले नाहीत.",
    loadingUploads: "लोड होत आहे…",
    noLayersYet:
      "अद्याप कोणतेही संपादनयोग्य स्तर नाहीत. येथे अपलोड केलेली कोणतीही गोष्ट सर्वेक्षण डेटाचा भाग न होता त्याच्या शेजारी नकाशावर दिसते, आणि सर्वेक्षक ती फील्ड अॅपमध्ये दुरुस्त करू शकतात.",
    hideLayer: "हा स्तर लपवा",
    showLayer: "हा स्तर दाखवा",
    actionsFor: (name: string) => `${name} साठी कृती`,
    hideLayerMenu: "स्तर लपवा",
    showLayerMenu: "स्तर दाखवा",
    zoomToLayer: "स्तरावर झूम करा",
    viewTable: "तक्ता पहा",
    export: "एक्सपोर्ट",
    deleteLayer: "स्तर हटवा",
    deleteLayerTitle: "हा स्तर हटवायचा?",
    deleteLayerBodySuffix: (count: string) =>
      `आणि त्याची ${count} फिचर्स काढली जातील, यात क्षेत्रात केलेल्या दुरुस्त्यांचाही समावेश आहे. कोणत्याही सर्वेक्षण डेटावर परिणाम होणार नाही — हा स्तर केवळ संदर्भ भूमिती आहे.`,
    deleteLayerConfirmLabel: "स्तर हटवा",
    updateFromFile: "फाईलमधून अद्ययावत करा…",
    mergeTitle: (name: string) => `${name} अद्ययावत करा`,
    mergeIntro: (file: string) =>
      `${file} मधून: नवीन आकार जोडले जातील, स्तरात आधीच असलेले आकार वगळले जातील, आणि बदललेल्या आकारांना नवीन बाह्यरेषा मिळेल. काहीही काढले जात नाही.`,
    mergeKey: "आकार जुळवण्याचा आधार",
    mergeNoKey: "फक्त बाह्यरेषा (ID गुणधर्म नाही)",
    mergeAdded: "नवीन आकार जोडले जातील",
    mergeReshaped: "आकारांना नवीन बाह्यरेषा मिळेल",
    mergeAttrs: "आकारांना नसलेले गुणधर्म मिळतील",
    mergeUnchanged: "आधीच स्तरात आहेत, वगळले",
    mergeSkipped: "वगळले (पुनरावृत्त किंवा अस्पष्ट ID, किंवा वापरण्यायोग्य भूमिती नाही)",
    mergeNote: (count: string) =>
      `स्तरात ${count} आकार असतील. स्तरातील विद्यमान गुणधर्म मूल्ये (क्षेत्रातील दुरुस्त्यांसह) तशीच ठेवली जातात.`,
    mergeApply: "स्तर अद्ययावत करा",
    mergeDone: (name: string, added: number, reshaped: number) =>
      `${name} अद्ययावत: ${added} जोडले, ${reshaped} बदलले.`,
  },

  editableLayerTableDialog: {
    attributeTableFallback: "गुणधर्म तक्ता",
    loading: "लोड होत आहे…",
    featureCount: (countText: string) => `${countText} फिचर`,
    propertyCount: (count: number) => ` · ${count} गुणधर्म`,
    rowsEditedCount: (count: number) => ` · ${count} पंक्ती संपादित`,
    loadingAttributes: "गुणधर्म लोड होत आहेत…",
    noFeatures: "या स्तरामध्ये कोणतेही फिचर नाहीत.",
    noProperties: (countText: string) => `या ${countText} फिचर्समध्ये कोणतेही गुणधर्म नाहीत.`,
    saveRowHeader: "पंक्ती जतन करा",
    saveRowTitle: (row: number) => `फक्त पंक्ती ${row} जतन करा, इतर संपादित पंक्ती प्रलंबित ठेवा`,
    nothingEditedRow: "या पंक्तीत काहीही संपादित केलेले नाही",
    discard: "टाकून द्या",
    saveAllRows: (count: number) => `सर्व ${count} पंक्ती जतन करा`,
    saving: "जतन करत आहे…",
    done: "पूर्ण झाले",
    saveOrDiscardFirst: "आधी तुमचे बदल जतन करा किंवा टाकून द्या",
    edit: "संपादित करा",
    cellAriaLabel: (col: string, row: number) => `${col}, पंक्ती ${row}`,
    rowRange: (from: string, to: string, total: string) => `${total} पैकी ${from}–${to}`,
    firstPage: "पहिले पान",
    previousPage: "मागील पान",
    nextPage: "पुढील पान",
    lastPage: "शेवटचे पान",
    rowsPerPage: "प्रति पान पंक्ती",
    perPage: (count: number) => `${count} / पान`,
  },

  scopeTree: {
    intro: "कोणत्याही स्तरावर खूण करून नेमणूक द्या. खूण त्याखालील सर्व काही व्यापते, नंतर आयात केलेल्या पंक्तींसह.",
    loadingHierarchy: "प्रकल्प आणि संरचना लोड होत आहे…",
    noProjectsToAssign: "नेमणूक देण्यासाठी कोणतेही प्रकल्प नाहीत.",
    nothingImportedYet: "या स्तरावर अद्याप काहीही आयात केलेले नाही.",
    collapse: "संकुचित करा",
    expand: "विस्तृत करा",
    alreadyCovered: "वरच्या एका नेमणुकीद्वारे आधीच व्यापलेले",
    partialHint: "यामध्ये काहीतरी नेमून दिलेले आहे. संपूर्ण गोष्ट नेमून देण्यासाठी खूण करा.",
    // TODO(mr-review): "partial" (a checkbox's indeterminate-state tag) —
    // translated for sense ("अंशतः" = "partially") rather than a fixed UI term.
    partial: "अंशतः",
    levelProject: "प्रकल्प",
    levelZone: "झोन",
    levelWard: "प्रभाग",
    levelBlock: "ब्लॉक",
    nothingAssigned: "काहीही नेमून दिलेले नाही — या खात्याला काहीही दिसणार नाही.",
    assignmentsCount: (count: number) => `${count} नेमणुका`,
    cancel: "रद्द करा",
    save: "जतन करा",
    couldntLoadProjects: "प्रकल्प लोड करता आले नाहीत.",
    couldntLoadLevel: "तो स्तर लोड करता आला नाही.",
  },

  hierarchyFilterBar: {
    allZones: "सर्व झोन",
    allWardsPlaceholder: "सर्व प्रभाग",
    allBlocksPlaceholder: "सर्व ब्लॉक",
    selectWardFirst: "आधी प्रभाग निवडा",
    allWards: (count: number) => `सर्व प्रभाग (${count})`,
    allBlocks: (count: number) => `सर्व ब्लॉक (${count})`,
    clear: "साफ करा",
    selectedOnly: "फक्त निवडलेले",
    selectedOnlyTitle: "चालू: नकाशावर फक्त निवडलेला झोन, प्रभाग किंवा ब्लॉक दिसतो. बंद: नकाशावर सर्व दिसते.",
    shownOfTotal: (shown: string, total: string) => `${total} पैकी ${shown}, यामध्ये`,
  },

  assignmentProgressDialog: {
    assignmentProgress: "नेमणूक प्रगती",
    workingItOut: "गणना करत आहे…",
    couldntLoadProgress: "प्रगती लोड करता आली नाही.",
    adminNote: "हा एक प्रशासक आहे. तो प्रत्येक प्रकल्पापर्यंत पोहोचू शकतो, त्यामुळे त्याच्याकडे ट्रॅक करण्यासाठी कोणतीही नेमणूक नाही.",
    nothingAssignedYetPrefix:
      "अद्याप काहीही नेमून दिलेले नाही. या खात्याला काम करण्यासाठी प्रकल्प, झोन, प्रभाग किंवा ब्लॉक देण्यासाठी",
    assignmentWord: "नेमणूक",
    nothingAssignedYetSuffix: "वापरा.",
    blocksAssigned: (count: number) => `${count} ब्लॉक नेमून दिले`,
    percentDone: (pct: number | null) => `${pct}% पूर्ण`,
    submittedLabel: "सादर केले",
    inProgressLabel: "प्रगतीपथावर",
    notStartedLabel: "सुरू झाले नाही",
    propertiesInBlocksTitle: "त्या ब्लॉकमधील मालमत्ता",
    numberedOfTotal: (numbered: string, total: string) => `${total} पैकी क्रमांकित`,
    stillToDo: (count: string) => `${count} अजून बाकी`,
    byProject: "प्रकल्पानुसार",
    blocksFraction: (submitted: number, total: number) => `${total} पैकी ${submitted} ब्लॉक`,
    collectedBySurveyor: "या सर्वेक्षकाने संकलित केलेले",
    properties: "मालमत्ता",
    polygons: "बहुभुज",
    pinned: (count: number) => `+${count} पिन केलेले`,
    today: "आज",
    yesterday: "काल",
    nothingCollectedYet:
      "या खात्याअंतर्गत अद्याप काहीही संकलित केलेले नाही. क्षेत्रात घर क्रमांक दिल्यावर एक मालमत्ता येथे मोजली जाते.",
    months: ["जाने", "फेब्रु", "मार्च", "एप्रि", "मे", "जून", "जुलै", "ऑग", "सप्टें", "ऑक्टो", "नोव्हें", "डिसें"],
  },

  newProjectForm: {
    newProject: "नवीन प्रकल्प",
    projectNameLabel: "प्रकल्पाचे नाव",
    // TODO(mr-review): placeholder EXAMPLE text, not UI chrome — left in
    // English since it models what someone would literally type (a project
    // title), matching the English copy's own example.
    projectNamePlaceholder: "Thergaon Zone 4 — 2026 resurvey",
    descriptionLabel: "वर्णन",
    optional: "(ऐच्छिक)",
    descriptionPlaceholder: "प्रभाग 12 सीमा पुनर्सर्वेक्षण",
    creating: "तयार करत आहे…",
    create: "तयार करा",
    cancel: "रद्द करा",
    couldntCreateProject: "प्रकल्प तयार करता आला नाही.",
  },

  slidePanel: {
    closePanel: "पॅनेल बंद करा",
  },

  mapPreview: {
    boundaryZones: "झोन",
    boundaryWards: "प्रभाग",
    boundaryBlocks: "ब्लॉक",
    imagery: "इमेजरी",
    fieldData: "क्षेत्रीय डेटा",
    propertyNumbers: "मालमत्ता क्रमांक",
    collapseBoundaries: "सीमा संकुचित करा",
    expandBoundaries: "सीमा विस्तृत करा",
    showHideAllBoundaries: "सर्व सीमा स्तर दाखवा किंवा लपवा",
    boundaries: "सीमा",
    view: "दृश्य",
    satelliteMap: "उपग्रह नकाशा",    highlightProgress: "प्रगती ठळक करा",
    inProgress: "प्रगतीपथावर",
    submitted: "सादर केले",
    notStarted: "सुरू झाले नाही",
    zoneWardBlockNames: "झोन / प्रभाग / ब्लॉक नावे",
    boundaryLabels: "लेबल",
    propertyNumbersOnLayers: "स्तरांवरील मालमत्ता क्रमांक",
    plotNumbersOnLayers: "स्तरांवरील प्लॉट क्रमांक",
    fieldDataOutlineOnly: "फील्ड डेटा: फक्त सीमा",
    layersOutlineOnly: "स्तर: फक्त सीमा",
    outlineOnlyHint: "चालू: आकार भरणाशिवाय फक्त सीमारेषा म्हणून दिसतात, खालचा नकाशा दिसतो. बंद: नेहमीप्रमाणे भरलेले.",
    zoomInForNumbers: "मालमत्ता क्रमांक पाहण्यासाठी झूम इन करा",
    plotPrefix: "प्लॉट",
    noNumber: "क्रमांक नाही",
    byUser: (name: string) => `${name} द्वारे`,
    couldntLoadLayer: "हा स्तर लोड करता आला नाही",
    markedForRevisit: "पुनर्भेटीसाठी चिन्हांकित",
    qaCheck: "ओव्हरलॅप व स्लिव्हर तपासणी",
    qaOverlaps: "ओव्हरलॅप",
    qaSlivers: "स्लिव्हर",
    legend: "नकाशा सूची",
  },
  workspace: {
    filtersAndLayers: "फिल्टर व स्तर",
    collapsePanel: "फिल्टर व स्तर पॅनेल लपवा",
    openPanel: "फिल्टर व स्तर पॅनेल दाखवा",
    filterTab: "फिल्टर",
    layerTab: "स्तर",
    zone: "झोन",
    ward: "प्रभाग",
    block: "ब्लॉक",
    noHierarchy: "या प्रकल्पासाठी अद्याप झोन, प्रभाग किंवा ब्लॉक आयात केलेले नाहीत.",
    selectedAreaOnly: "फक्त निवडलेले क्षेत्र दाखवा",
    selectedAreaOnlyHint: "चालू: नकाशावर फक्त निवडलेला झोन, प्रभाग किंवा ब्लॉक दिसतो. बंद: नकाशावर सर्व दिसते; तक्ता फिल्टर केलेला राहतो.",
    allOfProject: (n: string) => `या प्रकल्पातील सर्व ${n} मालमत्ता.`,
    resetFilters: "सर्व फिल्टर रीसेट करा",
    baseMap: "मूळ नकाशा",
    street: "रस्ते",
    satellite: "उपग्रह",
    none: "काहीही नाही",
    drone: "ड्रोन",
    boundaries: "सीमा",
    showAll: "सर्व दाखवा",
    hideAll: "सर्व लपवा",
    surveyData: "सर्वेक्षण डेटा",
    numbersHint: "जवळ झूम केल्यावर मालमत्ता क्रमांक दिसतात",
    manage: "व्यवस्थापन",
    noEditableLayers: "कोणतेही संपादनयोग्य स्तर अपलोड केलेले नाहीत.",
    analysis: "विश्लेषण",
    manageUploads: "अपलोड व्यवस्थापन",
    manageUploadsHint: "या प्रकल्पासाठी ड्रोन प्रतिमा, मालमत्ता बाह्यरेषा आणि संपादनयोग्य स्तर.",
    surveyorSubtitle: "तुमच्या नेमलेल्या क्षेत्रांचा क्षेत्रीय डेटा.",
    cardProperties: "मालमत्ता",
    cardNumbered: "क्रमांकित",
    cardSubmitted: "सादर केलेले ब्लॉक",
    cardInProgress: "प्रगतीतील ब्लॉक",
    cardNotStarted: "सुरू न झालेले ब्लॉक",
    cardImagery: "तयार प्रतिमा",
    cardRevisit: "पुनर्भेटीसाठी",
    ofTotal: (n: string) => `/ ${n}`,
    ofBlocks: (n: string) => `/ ${n}`,
    fieldDataTitle: (n: string) => `क्षेत्रीय डेटा · ${n}`,
    showTable: "तक्ता दाखवा",
    hideTable: "तक्ता लपवा",
    dragToResize: "आकार बदलण्यासाठी ओढा",
    maximiseTable: "तक्त्याला अधिक जागा द्या",
    restoreTable: "नकाशा पूर्ववत करा",
  },
  surveyTimingPage: {
    title: "सर्वेक्षण वेळ",
    filterFrom: "पासून",
    filterTo: "पर्यंत",
    filterZone: "झोन",
    filterWard: "प्रभाग",
    allZones: "सर्व झोन",
    allWards: "सर्व प्रभाग",
    apply: "लागू करा",
    clear: "साफ करा",
    statPropertiesSurveyed: "सर्वेक्षण केलेल्या मालमत्ता",
    statBlocksSubmitted: "सादर केलेले ब्लॉक",
    statActiveSurveyors: "सक्रिय सर्वेक्षक",
    tabByUser: "सर्वेक्षकानुसार",
    tabByBlock: "ब्लॉकनुसार",
    tabByWard: "प्रभागानुसार",
    panelByUser: (n: string) => `सर्वेक्षकानुसार (${n})`,
    panelByBlock: (n: string) => `ब्लॉकनुसार (${n})`,
    panelByWard: (n: string) => `प्रभागानुसार (${n})`,
    noSurveysTitle: "सर्वेक्षण नोंदवलेले नाही",
    noSurveysBody: "या व्याप्तीत अद्याप कोणत्याही मालमत्तेला क्रमांक दिलेला नाही.",
    noBlocksTitle: "ब्लॉक आढळले नाहीत",
    noBlocksBody: "या व्याप्तीत कोणतेही ब्लॉक नाहीत.",
    noWardsTitle: "प्रभाग आढळले नाहीत",
    noWardsBody: "या व्याप्तीत कोणतेही प्रभाग नाहीत.",
    colSurveyor: "सर्वेक्षक",
    colPropertiesSurveyed: "सर्वेक्षण केलेल्या मालमत्ता",
    colFirstSurvey: "पहिले सर्वेक्षण",
    colLastSurvey: "शेवटचे सर्वेक्षण",
    colAvgPropertyDuration: "मालमत्तेचा सरासरी कालावधी",
    colBlock: "ब्लॉक",
    colWard: "प्रभाग",
    colStatus: "स्थिती",
    colClaimed: "स्वीकारले",
    colSubmitted: "सादर केले",
    colBlockDuration: "ब्लॉक कालावधी",
    colAvgPerProperty: "सरासरी / मालमत्ता",
    colProperties: "मालमत्ता",
    colSurveyed: "सर्वेक्षण केले",
    colZone: "झोन",
    colBlocksTotal: "एकूण ब्लॉक",
    colSurveyors: "सर्वेक्षक",
    statusPending: "प्रलंबित",
    statusInProgress: "प्रगतीपथावर",
    statusSubmitted: "सादर केले",
    durationSeconds: (n: number) => `${n} से`,
    durationMinutes: (n: number) => `${n} मि`,
    durationHoursMinutes: (h: number, m: number) => `${h} ता ${m} मि`,
    durationHours: (h: number) => `${h} ता`,
  },
  accountSecurity: {
    button: "फोन व सत्र",
    title: "फोन व सत्र",
    description: "नोंदणीकृत फोन, साइन-इन सत्रे आणि लॉक —",
    loading: "लोड होत आहे…",
    badgeOnline: "आत्ता ऑनलाइन",
    badgeSignedIn: "साइन इन",
    badgeLastSeen: (when: string) => `शेवटचे दिसले ${when}`,
    badgeNeverSignedIn: "कधीही साइन इन नाही",
    badgeNoPhone: "फोन नोंदणीकृत नाही",
    badgeLocked: (min: number) => `${min} मि लॉक`,
    badgeLateUploads: (n: number) => `${n} उशिरा अपलोड`,
    deviceHeading: "नोंदणीकृत फोन",
    deviceNotBound: "कार्यालयीन खाती फोनशी जोडलेली नाहीत.",
    deviceNone: "फोन नोंदणीकृत नाही. फील्ड ॲपमधून पुढील साइन-इन त्या फोनची नोंदणी करेल.",
    deviceModel: "फोन",
    deviceId: "डिव्हाइस आयडी",
    deviceAndroid: "Android",
    deviceApp: "ॲप आवृत्ती",
    deviceRegistered: "नोंदणी",
    deviceLastLogin: "शेवटचे साइन-इन",
    resetDevice: "फोन रीसेट करा…",
    resetDeviceTitle: "नोंदणीकृत फोन रीसेट करायचा?",
    resetDeviceBody: (user: string) =>
      `${user} आत्ता साइन आउट होईल आणि फोन मुक्त होईल. फील्ड ॲपमधून पुढील साइन-इन — कोणत्याही फोनवर — त्या फोनची नोंदणी करेल. फोन बदलला, हरवला किंवा खराब झाला तर हे वापरा.`,
    resetDeviceConfirm: "फोन रीसेट करा",
    previousDevices: "आधीचे फोन",
    releasedBy: (when: string, by: string) => `${when} रोजी मुक्त${by ? ` — ${by}` : ""}`,
    sessionHeading: "सत्रे",
    sessionNone: "अद्याप साइन-इन नाही.",
    sessionActive: "सक्रिय",
    sessionEnded: "संपले",
    sessionExpired: "मुदत संपली",
    sessionStarted: "सुरुवात",
    sessionLastActivity: "शेवटची हालचाल",
    sessionIp: "IP पत्ता",
    sessionDevice: "डिव्हाइस",
    sessionBrowser: "ब्राउझर (पोर्टल)",
    endReason: {
      logout: "साइन आउट",
      replaced: "पुन्हा साइन इन",
      force_logout: "सक्तीने लॉगआउट",
      device_reset: "फोन रीसेट",
      password_reset: "पासवर्ड बदलला",
      idle: "दीर्घकाळ निष्क्रिय",
      device_blocked: "फोन ब्लॉक",
      account_disabled: "खाते बंद",
      session_revoked: "कार्यालयाने संपवले",
    } as Record<string, string>,
    finalUploadPending: "ऑफलाइन काम अजून अपलोड करू शकतो",
    finalUploadDone: "अंतिम अपलोड पूर्ण",
    forceLogout: "सक्तीने लॉगआउट…",
    forceLogoutTitle: "सक्तीने लॉगआउट करायचे?",
    forceLogoutBody: (user: string) =>
      `${user} पुढच्याच कृतीवर सर्वत्र साइन आउट होईल. ते पासवर्डने पुन्हा साइन इन करू शकतात.`,
    forceLogoutConfirm: "सक्तीने लॉगआउट",
    noActiveSession: "सध्या कुठेही साइन इन नाही.",
    allowFinalSync: "आधी फोनला ऑफलाइन गोळा केलेले काम अपलोड करू द्या",
    allowFinalSyncHint:
      "शिफारस. लॉगआउटनंतरचे अपलोड खाली तपासणीसाठी दाखवले जातात. हरवलेल्या किंवा चोरीला गेलेल्या फोनसाठी खूण काढा.",
    lockHeading: "साइन-इन लॉक",
    lockedFor: (min: number) => `खूप चुकीचे पासवर्ड — आणखी ${min} मिनिटे लॉक.`,
    notLocked: "लॉक नाही.",
    failedSoFar: (n: number) => `सलग ${n} चुकीचे पासवर्ड.`,
    unlock: "आत्ता अनलॉक करा",
    lateHeading: "सत्र संपल्यानंतर अपलोड झालेले",
    lateIntro:
      "कार्यालयाने साइन आउट केल्यानंतर फोनने पाठवलेल्या मालमत्ता (एकदाच अंतिम अपलोड). वापरण्यापूर्वी तपासा.",
    lateNone: "काहीही नाही.",
    lateProperty: "मालमत्ता",
    lateBlock: "ब्लॉक",
    lateAction: "बदल",
    lateWhen: "केव्हा",
    lateDeleted: "नंतर हटवली",
    lateActions: { create: "जोडली", update: "संपादित", delete: "हटवली" } as Record<string, string>,
    lateMore: (shown: number, total: number) => `${total} पैकी नवीनतम ${shown} दाखवत आहे.`,
    noticeLoggedOut: (user: string) => `${user} साइन आउट झाले.`,
    noticeDeviceReset: (user: string) => `${user} चा फोन रीसेट झाला. पुढील साइन-इन फोनची नोंदणी करेल.`,
    noticeUnlocked: (user: string) => `${user} अनलॉक झाले.`,
    badgePending: "फोन मंजुरीच्या प्रतीक्षेत",
    badgeDisabled: "बंद",
    deviceNoneApproval: "फोन नोंदणीकृत नाही. फील्ड ॲपमधून साइन इन करणारा फोन येथे मंजुरीसाठी थांबतो.",
    pendingHeading: "मंजुरीच्या प्रतीक्षेतील फोन",
    pendingReplaces: (model: string) => `मंजूर केल्यास ${model} बदलला जाईल`,
    pendingLastTry: (when: string) => `शेवटचा प्रयत्न ${when}`,
    approve: "मंजूर करा",
    reject: "नाकारा",
    rejectBlock: "नाकारा व ब्लॉक करा",
    noticeApproved: (user: string) => `${user} साठी फोन मंजूर. ते आता साइन इन करू शकतात.`,
    noticeRejected: (user: string) => `${user} साठी फोन नाकारला.`,
    blockDevice: "फोन ब्लॉक करा…",
    blockDeviceTitle: "हा फोन ब्लॉक करायचा?",
    blockDeviceBody: (user: string) =>
      `${user} आत्ता साइन आउट होईल आणि अनब्लॉक करेपर्यंत (फोन व सत्रे पान) हा फोन कोणत्याही खात्यात साइन इन करू शकणार नाही. त्यानंतर ${user} साठी दुसरा फोन नोंदवता येईल. हरवलेल्या किंवा चोरीला गेलेल्या फोनसाठी हे वापरा.`,
    blockDeviceConfirm: "फोन ब्लॉक करा",
    noticeDeviceBlocked: (user: string) => `${user} चा फोन ब्लॉक झाला.`,
    endSession: "संपवा",
    endSessionTitle: "हे सत्र संपवायचे?",
    endSessionBody: (user: string) =>
      `${user} चे हे एक साइन-इन पुढच्या कृतीवर बंद होईल. त्यांची इतर सत्रे चालू राहतील.`,
    endSessionConfirm: "सत्र संपवा",
    noticeSessionEnded: "सत्र संपले.",
    sessionLimit: (n: number) =>
      n === 0
        ? "एकाच वेळी सत्रांवर मर्यादा नाही."
        : `एकाच वेळी जास्तीत जास्त ${n} सत्रे; पुन्हा साइन इन केल्यास सर्वात जुने बंद होते.`,
    accountHeading: "खाते प्रवेश",
    accountActive: "सक्रिय. खाते साइन इन करू शकते.",
    accountDisabledText: (by: string, when: string) =>
      `${when} रोजी बंद केले${by ? ` — ${by}` : ""}. पुन्हा सुरू करेपर्यंत साइन-इन नाकारले जाईल.`,
    disable: "खाते बंद करा…",
    enable: "खाते सुरू करा",
    disableTitle: "हे खाते बंद करायचे?",
    disableBody: (user: string) =>
      `${user} सर्वत्र साइन आउट होईल आणि खाते पुन्हा सुरू करेपर्यंत साइन इन करू शकणार नाही. काहीही हटवले जात नाही.`,
    disableConfirm: "खाते बंद करा",
    noticeDisabled: (user: string) => `${user} बंद केले.`,
    noticeEnabled: (user: string) => `${user} पुन्हा सुरू केले.`,
    close: "बंद करा",
  },
  sessionsPage: {
    crumb: "फोन व सत्रे",
    heading: "फोन व सत्रे",
    subheading: "सध्या कोण साइन इन आहे, मंजुरीच्या प्रतीक्षेतील फोन आणि ब्लॉक केलेले फोन.",
    refresh: "रीफ्रेश",
    pendingHeading: "मंजुरीच्या प्रतीक्षेतील फोन",
    pendingNone: "कोणताही फोन प्रतीक्षेत नाही.",
    activeHeading: "सध्या साइन इन",
    activeNone: "कोणीही साइन इन नाही.",
    blockedHeading: "ब्लॉक केलेले फोन",
    blockedNone: "कोणताही फोन ब्लॉक नाही.",
    colAccount: "खाते",
    colDevice: "डिव्हाइस",
    colStarted: "साइन इन",
    colLastActivity: "शेवटची हालचाल",
    colIp: "IP पत्ता",
    colReason: "कारण",
    colBlocked: "ब्लॉक",
    idleFor: (min: number) => (min < 1 ? "आत्ता सक्रिय" : `${min} मि निष्क्रिय`),
    unblock: "अनब्लॉक करा",
    noticeUnblocked: (model: string) => `${model} अनब्लॉक झाला.`,
    blockedBy: (when: string, by: string) => `${when}${by ? ` — ${by}` : ""}`,
  },
  blockControls: {
    claimedBy: (name: string, when: string) => `${name} यांनी ${when} पासून दावा केला आहे`,
    lastActivity: (when: string) => `शेवटची कृती ${when}`,
    expires: (when: string) => `निष्क्रिय राहिल्यास दावा ${when} रोजी संपेल`,
    notClaimed: "कोणीही दावा केलेला नाही",
    release: "मुक्त करा",
    assign: "सोपवा",
    reassign: "दुसऱ्याला सोपवा",
    history: "इतिहास",
    releaseTitle: "हा ब्लॉक मुक्त करायचा?",
    releaseBody: (block: string, holder: string) =>
      `${block} वर ${holder} यांचा दावा राहणार नाही आणि नियुक्त कोणताही सर्वेक्षक त्यावर दावा करू शकेल. आधीच अपलोड झालेली सर्व माहिती ज्यांनी गोळा केली त्यांच्या नावाने ब्लॉकवरच राहील, आणि ${holder} यांच्या फोनवरील बाकी काम अजूनही अपलोड होऊ शकेल.`,
    reassignTitle: "हा ब्लॉक दुसऱ्या सर्वेक्षकाला द्या",
    reassignBody: (block: string) =>
      `आपण निवडलेले सर्वेक्षक आतापासून ${block} सांभाळतील. आधीच अपलोड झालेली सर्व माहिती ज्यांनी गोळा केली त्यांच्या नावाने ब्लॉकवरच राहील, आणि आधीच्या सर्वेक्षकाच्या फोनवरील बाकी काम अजूनही अपलोड होऊ शकेल.`,
    surveyor: "सर्वेक्षक",
    chooseSurveyor: "सर्वेक्षक निवडा…",
    reopenTitle: "सर्वेक्षकाकडे परत पाठवा",
    reopenBody:
      "खालील वेळेपर्यंत दुरुस्तीसाठी ब्लॉक पुन्हा उघडेल, नंतर आपोआप लॉक होईल. पुन्हा सादर केल्यावरही तो लगेच लॉक होतो. मंजूर करणारे अधिकारी म्हणून आपली नोंद होईल.",
    reopenFor: "दुरुस्ती कोण करेल",
    reopenForHolder: (name: string) => `${name} (ज्यांच्याकडे होता)`,
    reopenForNobody: "कोणीही नाही — नियुक्त कोणताही सर्वेक्षक दावा करू शकतो",
    reasonLabel: "कारण",
    reasonPlaceholder: "काय दुरुस्त करायचे आहे आणि का",
    reasonHint: "आवश्यक, किमान 10 अक्षरे.",
    validUntilLabel: "पर्यंत उघडा",
    validUntilHint: "आवश्यक. या वेळेनंतर ब्लॉक पुन्हा सादर झाला नसला तरी लॉक होईल.",
    reopenedUntil: (when: string, by: string) => `${when} पर्यंत दुरुस्तीसाठी पुन्हा उघडला, ${by} यांनी मंजूर केले.`,
    historyTitle: (block: string) => `${block} चा इतिहास`,
    historyNone: "अद्याप काहीही नोंदलेले नाही.",
    by: (name: string) => `${name} यांनी`,
    eventLabel: (e: string) =>
      ({
        claim: "दावा केला",
        claim_resumed: "दावा पुन्हा सुरू",
        claim_reopened: "दुरुस्तीसाठी दावा",
        claim_expired: "दावा संपला (निष्क्रिय)",
        release: "मुक्त केला",
        reassign: "दुसऱ्याला सोपवला",
        submit: "सादर केला",
        resubmitted: "पुन्हा सादर — पुन्हा लॉक",
        reopen_requested: "पुन्हा उघडण्याची विनंती",
        reopen_approved: "पुन्हा उघडणे मंजूर",
        reopen_rejected: "पुन्हा उघडणे नाकारले",
        relocked_expired: "पुन्हा लॉक — मुदत संपली",
        paired: "दुसऱ्या ब्लॉकसोबत जोडला",
        unpaired: "जोडणी संपली",
      })[e] ?? e,
    pairedWith: (block: string) => `${block} सोबत एकत्र सर्वेक्षण`,
    pairedHint: "आकार दोन्ही ब्लॉकमधील रेषा ओलांडू शकतात, घर क्रमांक दोन्हीत समान मालिकेत असतात, आणि एक सादर केल्यावर दोन्ही सादर होतात.",
    endPair: "जोडणी संपवा",
    endPairTitle: "ही जोडणी संपवायची?",
    endPairBody: (a: string, b: string) =>
      `${a} आणि ${b} पुन्हा वेगवेगळे सर्वेक्षित होतील. दोन्ही त्याच सर्वेक्षकाकडे राहतील, आणि प्रत्येक आकार ज्या ब्लॉकमध्ये नोंदलेला आहे तिथेच राहील.`,
    noticePairEnded: (a: string, b: string) => `${a} आणि ${b} आता जोडलेले नाहीत.`,
    noticeReleased: (block: string) => `${block} मुक्त केला.`,
    noticeReassigned: (block: string, user: string) => `${block} आता ${user} यांच्याकडे आहे.`,
    noticeReopened: (block: string, until: string) => `${block} ${until} पर्यंत दुरुस्तीसाठी उघडा आहे.`,
  },
  reopenRequestsPage: {
    crumb: "पुन्हा उघडण्याच्या विनंत्या",
    heading: "पुन्हा उघडण्याच्या विनंत्या",
    subheading:
      "सादर केलेला ब्लॉक दुरुस्त करण्यासाठी सर्वेक्षक विनंती करतात. मंजुरीसाठी समाप्ती वेळ आणि नाकारण्यासाठी कारण आवश्यक आहे. पुन्हा उघडलेला ब्लॉक पुन्हा सादर केल्यावर किंवा मुदत संपल्यावर पुन्हा लॉक होतो.",
    refresh: "रिफ्रेश",
    pendingHeading: "निर्णयाच्या प्रतीक्षेत",
    pendingNone: "कोणतीही विनंती प्रलंबित नाही.",
    historyHeading: "आधीच्या विनंत्या",
    historyNone: "आधीच्या विनंत्या नाहीत.",
    colBlock: "ब्लॉक",
    colRequestedBy: "विनंती करणारे",
    colReason: "कारण",
    colStatus: "निकाल",
    colDecidedBy: "निर्णय घेणारे",
    colValidUntil: "पर्यंत उघडा",
    approve: "मंजूर करा",
    reject: "नाकारा",
    approveTitle: (block: string) => `${block} पुन्हा उघडायचा?`,
    approveBody: (who: string) =>
      `${who} यांना खालील वेळेपर्यंत दुरुस्तीसाठी ब्लॉक परत मिळेल. मंजूर करणारे अधिकारी म्हणून आपली नोंद होईल.`,
    rejectTitle: (block: string) => `${block} ची विनंती नाकारायची?`,
    rejectBody: (who: string) => `${who} यांना ॲपमध्ये आपले कारण दिसेल. ब्लॉक लॉकच राहील.`,
    rejectNote: "नाकारण्याचे कारण",
    noteOptional: "सर्वेक्षकासाठी टीप (ऐच्छिक)",
    forUser: (name: string) => `${name} साठी`,
    noticeApproved: (block: string) => `${block} पुन्हा उघडला.`,
    noticeRejected: (block: string) => `${block} ची विनंती नाकारली.`,
    status: {
      pending: "प्रतीक्षेत",
      approved: "सध्या उघडा",
      rejected: "नाकारली",
      expired: "मुदत संपली — पुन्हा लॉक",
      completed: "पुन्हा सादर — पुन्हा लॉक",
    } as Record<string, string>,
  },
  idle: {
    stillThereTitle: "तुम्ही अजून आहात का?",
    stillThereBody: (min: number, left: string) =>
      `${min} मिनिटे कोणतीही हालचाल नाही. ${left} मध्ये साइन आउट होईल.`,
    cancel: "रद्द करा — साइन इन राहा",
    signOutNow: "साइन आउट",
    signedOut: (min: number) => `${min} मिनिटे हालचाल नसल्याने साइन आउट झाले. पुन्हा साइन इन करा.`,
    timedOutTitle: "सत्र कालबाह्य झाले",
    ok: "ठीक आहे",
  },
} satisfies Translations;
